import React, { useState } from 'react';
import {
  Image as ImageIcon,
  Users,
  Package,
  Layout,
  Settings as SettingsIcon,
  Video,
  Menu,
  X
} from 'lucide-react';
import IllustrationManager from '../pages/IllustrationManager';
import DistributorManager from '../pages/DistributorManager';
import CategoryManager from '../pages/CategoryManager';
import FormatManager from '../pages/FormatManager';
import VideoManager from '../pages/VideoManager';
import InventoryManager from '../pages/InventoryManager';
import SettingsManager from '../pages/SettingsManager';
import LogoutButton from './LogoutButton';

const AdminLayout = ({ children }) => {
  const [activeTab, setActiveTab] = useState('illustrations');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    { id: 'illustrations', label: 'Illustrazioni', icon: ImageIcon },
    { id: 'categories', label: 'Categorie', icon: Package },
    { id: 'formats', label: 'Formati', icon: Layout },
    { id: 'videos', label: 'Video', icon: Video },
    { id: 'distributors', label: 'Distributori', icon: Users },
    { id: 'inventory', label: 'Magazzino', icon: Package },
    { id: 'settings', label: 'Impostazioni', icon: SettingsIcon },
  ];

  const handleTabChange = (id) => {
    setActiveTab(id);
    setSidebarOpen(false);
  };

  const renderContent = () => {
    switch(activeTab) {
      case 'illustrations': return <IllustrationManager />;
      case 'distributors': return <DistributorManager />;
      case 'categories': return <CategoryManager />;
      case 'formats': return <FormatManager />;
      case 'videos': return <VideoManager />;
      case 'inventory': return <InventoryManager />;
      case 'settings': return <SettingsManager />;
      default: return <IllustrationManager />;
    }
  };

  const activeLabel = menuItems.find(m => m.id === activeTab)?.label || activeTab;

  return (
    <div className="flex h-screen bg-brand-light text-brand-dark overflow-hidden">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-30
        w-64 bg-white border-r border-brand-main flex flex-col
        transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 md:p-6 border-b border-brand-main flex items-center justify-between">
          <h1 className="text-xl font-bold tracking-tight">Faby Art Admin</h1>
          <button
            className="lg:hidden p-1 rounded hover:bg-gray-100"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleTabChange(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === item.id
                ? 'bg-brand-main text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <item.icon size={20} />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-brand-main flex justify-between items-center text-xs text-gray-400">
          <span>© 2026 Illustrazioni e Poesia Minuta</span>
          <LogoutButton />
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar — hamburger visible only on mobile */}
        <header className="sticky top-0 bg-white border-b border-brand-main px-4 py-3 flex items-center gap-3 z-10 shrink-0">
          <button
            className="lg:hidden p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={22} />
          </button>
          <h2 className="text-lg font-semibold">{activeLabel}</h2>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-6xl mx-auto">
            {renderContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
